import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '@/components/ui/select';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from '../../components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Calendar, Plus, Pencil, Trash2, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../core/apiClient';

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: 'nacional' | 'autonomic' | 'local';
  recurring: boolean;
}

const HOLIDAY_TYPE_LABELS: Record<string, string> = {
  nacional: 'Nacional',
  autonomic: 'Autonomic',
  local: 'Local',
};

const HOLIDAY_TYPE_COLORS: Record<string, string> = {
  nacional: 'bg-red-100 text-red-800',
  autonomic: 'bg-amber-100 text-amber-800',
  local: 'bg-blue-100 text-blue-800',
};

export default function Holidays() {
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'nacional' as 'nacional' | 'autonomic' | 'local',
    recurring: true,
  });
  const [saving, setSaving] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  useEffect(() => {
    loadHolidays();
  }, [year]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ success: boolean; data: Holiday[] }>(`/holidays?year=${year}`);
      if (response.success && response.data) {
        setHolidays(response.data);
      }
    } catch (error) {
      console.error('Error loading holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultHolidays = async () => {
    try {
      setLoadingDefaults(true);
      await api.post(`/holidays/load-defaults/${year}`);
      await loadHolidays();
    } catch (error) {
      console.error('Error loading default holidays:', error);
    } finally {
      setLoadingDefaults(false);
    }
  };

  const openCreateModal = () => {
    setEditingHoliday(null);
    setFormData({
      name: '',
      date: `${year}-01-01`,
      type: 'nacional',
      recurring: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date.split('T')[0],
      type: holiday.type,
      recurring: holiday.recurring,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editingHoliday) {
        await api.put(`/holidays/${editingHoliday.id}`, formData);
      } else {
        await api.post('/holidays', formData);
      }
      setIsModalOpen(false);
      await loadHolidays();
    } catch (error) {
      console.error('Error saving holiday:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Segur que vols eliminar aquest festiu?')) return;
    try {
      await api.delete(`/holidays/${id}`);
      await loadHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ca-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/rrhh')}
            title="Tornar enrere"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calendar className="h-6 w-6" />
              Festius
            </h1>
            <p className="text-muted-foreground">Gestió del calendari de festius</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Year selector */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setYear(year - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-semibold text-sm w-16 text-center">{year}</span>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setYear(year + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button 
            variant="outline" 
            onClick={loadDefaultHolidays}
            disabled={loadingDefaults}
          >
            <Download className="h-4 w-4 mr-2" />
            {loadingDefaults ? 'Carregant...' : 'Carregar festius Barcelona'}
          </Button>
          <Button onClick={openCreateModal}>
            <Plus className="h-4 w-4 mr-2" />
            Afegir festiu
          </Button>
        </div>
      </div>

      {/* Holidays table */}
      <Card>
        <CardHeader>
          <CardTitle>Festius de l&apos;any {year}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Carregant...</div>
          ) : holidays.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No hi ha festius definits per a aquest any</p>
              <Button variant="outline" onClick={loadDefaultHolidays}>
                Carregar festius de Barcelona
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Tipus</TableHead>
                  <TableHead>Recurrent</TableHead>
                  <TableHead className="text-right">Accions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidays.map((holiday) => (
                  <TableRow key={holiday.id}>
                    <TableCell className="font-medium">
                      {formatDate(holiday.date)}
                    </TableCell>
                    <TableCell>{holiday.name}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${HOLIDAY_TYPE_COLORS[holiday.type]}`}>
                        {HOLIDAY_TYPE_LABELS[holiday.type]}
                      </span>
                    </TableCell>
                    <TableCell>
                      {holiday.recurring ? 'Si' : 'No'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openEditModal(holiday)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(holiday.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingHoliday ? 'Editar festiu' : 'Nou festiu'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nom</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Nadal"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipus</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                >
                  <option value="nacional">Nacional</option>
                  <option value="autonomic">Autonomic</option>
                  <option value="local">Local</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="recurring"
                  checked={formData.recurring}
                  onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="recurring" className="text-sm">
                  Repetir cada any
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel·lar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Desant...' : 'Desa'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
